import React, { useState, useEffect } from 'react';
import {
  FunnelIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import routingService from '../../services/routingService';

const MATCH_TYPES = [
  { value: 'table', label: 'Table Name', description: 'Route alerts for a specific table', placeholder: 'e.g., orders, users' },
  { value: 'metric', label: 'Metric Name', description: 'Route alerts for a specific metric', placeholder: 'e.g., row_count, null_rate' },
  { value: 'severity', label: 'Severity Level', description: 'Route all alerts of a certain severity', placeholder: 'critical, high, medium, low' },
  { value: 'validation_rule', label: 'Validation Rule', description: 'Route alerts for a specific validation', placeholder: 'Rule ID' },
  { value: 'pattern', label: 'Pattern (Wildcard)', description: 'Match multiple items with wildcards', placeholder: 'e.g., staging_*, *_backup' },
];

const SEVERITY_LEVELS = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-800' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' },
];

// `icon` is the Heroicon component reference (not an instance) so the
// consumer can size + color it inline. Matches the SnoozeModal /
// SnoozeSettings convention.
const CHANNELS = [
  { value: 'email', label: 'Email', icon: EnvelopeIcon },
  { value: 'slack', label: 'Slack', icon: ChatBubbleLeftRightIcon },
  { value: 'webhook', label: 'Webhook', icon: GlobeAltIcon },
];

const AlertRoutingSettings = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await routingService.getRoutingRules();
      setRules(response.rules || []);
    } catch (err) {
      setError(err.message || 'Failed to load routing rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleDeleteRule = async (ruleId) => {
    setDeletingId(ruleId);
    try {
      await routingService.deleteRoutingRule(ruleId);
      setRules(rules.filter(r => r.id !== ruleId));
    } catch (err) {
      setError(err.message || 'Failed to delete rule');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await routingService.toggleRuleActive(rule.id, !rule.is_active);
      setRules(rules.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
    } catch (err) {
      setError(err.message || 'Failed to update rule');
    }
  };

  const getMatchTypeInfo = (type) => MATCH_TYPES.find(t => t.value === type) || MATCH_TYPES[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <FunnelIcon className="h-6 w-6 text-gray-400 mr-2" />
            Alert Routing Rules
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Route alerts to different channels based on table, metric, severity, or custom patterns.
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchRules}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            New Rule
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Rules List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      ) : rules.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FunnelIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-sm font-medium text-gray-900">No routing rules</h3>
          <p className="mt-2 text-sm text-gray-500">
            Create routing rules to send specific alerts to different channels or people.
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create First Rule
          </button>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <ul className="divide-y divide-gray-200">
            {rules.sort((a, b) => a.priority - b.priority).map((rule) => {
              const matchTypeInfo = getMatchTypeInfo(rule.match_type);

              return (
                <li key={rule.id} className={`p-4 ${!rule.is_active ? 'bg-gray-50 opacity-60' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {rule.rule_name}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {rule.is_active ? 'Active' : 'Disabled'}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Priority: {rule.priority}
                        </span>
                      </div>

                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                        <span>
                          <strong>{matchTypeInfo.label}:</strong> {rule.match_value}
                        </span>
                        <span>
                          <strong>Min Severity:</strong> {rule.min_severity}
                        </span>
                      </div>

                      <div className="mt-2 flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Channels:</span>
                        {(rule.notification_channels || []).map(channel => {
                          const channelInfo = CHANNELS.find(c => c.value === channel);
                          return (
                            <span key={channel} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                              {channelInfo?.icon && (
                                <channelInfo.icon className="h-3 w-3 mr-1" />
                              )}
                              {channelInfo?.label || channel}
                            </span>
                          );
                        })}
                        {rule.slack_channel && (
                          <span className="text-xs text-gray-500">
                            → {rule.slack_channel}
                          </span>
                        )}
                      </div>

                      {rule.description && (
                        <p className="mt-1 text-xs text-gray-400">{rule.description}</p>
                      )}
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleToggleActive(rule)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                          rule.is_active
                            ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                            : 'text-green-700 bg-green-100 hover:bg-green-200'
                        }`}
                      >
                        {rule.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => setEditingRule(rule)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRule(rule.id)}
                        disabled={deletingId === rule.id}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Info Box */}
      <div className="rounded-md bg-blue-50 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <span className="text-xl"></span>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">How routing works</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Rules are evaluated in priority order (lower number = higher priority)</li>
                <li>The first matching rule determines where the alert is sent</li>
                <li>Use patterns for flexible matching: <code className="bg-blue-100 px-1 rounded">prod_*</code> matches all production tables</li>
                <li>Override the default Slack channel to route critical alerts to <code className="bg-blue-100 px-1 rounded">#critical-alerts</code></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingRule) && (
        <RoutingRuleModal
          isOpen={showCreateModal || !!editingRule}
          onClose={() => {
            setShowCreateModal(false);
            setEditingRule(null);
          }}
          rule={editingRule}
          onSaved={() => {
            setShowCreateModal(false);
            setEditingRule(null);
            fetchRules();
          }}
        />
      )}
    </div>
  );
};

// Modal for creating/editing routing rules
const RoutingRuleModal = ({ isOpen, onClose, rule, onSaved }) => {
  const [formData, setFormData] = useState({
    rule_name: '',
    description: '',
    match_type: 'table',
    match_value: '',
    notification_channels: ['email'],
    slack_channel: '',
    email_recipients: '',
    min_severity: 'low',
    priority: 100,
    is_active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (rule) {
      setFormData({
        rule_name: rule.rule_name || '',
        description: rule.description || '',
        match_type: rule.match_type || 'table',
        match_value: rule.match_value || '',
        notification_channels: rule.notification_channels || ['email'],
        slack_channel: rule.slack_channel || '',
        email_recipients: (rule.email_recipients || []).join(', '),
        min_severity: rule.min_severity || 'low',
        priority: rule.priority || 100,
        is_active: rule.is_active !== false
      });
    }
  }, [rule]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.rule_name.trim() || !formData.match_value.trim()) {
      setError('Rule name and match value are required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        email_recipients: formData.email_recipients
          ? formData.email_recipients.split(',').map(e => e.trim()).filter(Boolean)
          : []
      };

      if (rule) {
        await routingService.updateRoutingRule(rule.id, payload);
      } else {
        await routingService.createRoutingRule(payload);
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save rule');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChannelToggle = (channel) => {
    const channels = formData.notification_channels;
    if (channels.includes(channel)) {
      setFormData({ ...formData, notification_channels: channels.filter(c => c !== channel) });
    } else {
      setFormData({ ...formData, notification_channels: [...channels, channel] });
    }
  };

  if (!isOpen) return null;

  const matchTypeInfo = MATCH_TYPES.find(t => t.value === formData.match_type) || MATCH_TYPES[0];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
        <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {rule ? 'Edit Routing Rule' : 'Create Routing Rule'}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Rule Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rule Name *
              </label>
              <input
                type="text"
                value={formData.rule_name}
                onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                placeholder="e.g., Critical alerts to #urgent"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            {/* Match Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Match Type
              </label>
              <select
                value={formData.match_type}
                onChange={(e) => setFormData({ ...formData, match_type: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                {MATCH_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">{matchTypeInfo.description}</p>
            </div>

            {/* Match Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Match Value *
              </label>
              <input
                type="text"
                value={formData.match_value}
                onChange={(e) => setFormData({ ...formData, match_value: e.target.value })}
                placeholder={matchTypeInfo.placeholder}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            {/* Min Severity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Severity
              </label>
              <select
                value={formData.min_severity}
                onChange={(e) => setFormData({ ...formData, min_severity: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                {SEVERITY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">Only route alerts at this severity or higher</p>
            </div>

            {/* Channels */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notification Channels
              </label>
              <div className="flex flex-wrap gap-2">
                {CHANNELS.map(channel => (
                  <button
                    key={channel.value}
                    type="button"
                    onClick={() => handleChannelToggle(channel.value)}
                    className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      formData.notification_channels.includes(channel.value)
                        ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-500'
                        : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                    }`}
                  >
                    {channel.icon && (
                      <channel.icon className="h-4 w-4 mr-1.5" />
                    )}
                    {channel.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Slack Channel Override */}
            {formData.notification_channels.includes('slack') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slack Channel (optional override)
                </label>
                <input
                  type="text"
                  value={formData.slack_channel}
                  onChange={(e) => setFormData({ ...formData, slack_channel: e.target.value })}
                  placeholder="#critical-alerts"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Leave empty to use the default channel</p>
              </div>
            )}

            {/* Email Recipients Override */}
            {formData.notification_channels.includes('email') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Recipients (optional override)
                </label>
                <input
                  type="text"
                  value={formData.email_recipients}
                  onChange={(e) => setFormData({ ...formData, email_recipients: e.target.value })}
                  placeholder="oncall@company.com, team-lead@company.com"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">Comma-separated. Leave empty to use default recipients.</p>
              </div>
            )}

            {/* Priority */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 100 })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Lower number = higher priority. First matching rule wins.</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                placeholder="Why this rule exists..."
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : (rule ? 'Save Changes' : 'Create Rule')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AlertRoutingSettings;
